import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import AuthCallback from './components/auth/AuthCallback';
import PasswordReset from './components/auth/PasswordReset';
import { CoolClimateNavigation } from './components/layout/CoolClimateNavigation';
import { Footer } from './components/layout/Footer';
import { Header } from './components/layout/Header';
import { Navigation } from './components/layout/Navigation';
import { CoolClimateCalculator } from './components/pages/CoolClimateCalculator';
import { Energy } from './components/pages/Energy';
import { GHGInventory } from './components/pages/GHGInventory';
import { Other } from './components/pages/Other';
import { Overview } from './components/pages/Overview';
import { People } from './components/pages/People';
import { Community } from './components/pages/Community';
import SimpleDevelopersLogPage from './components/pages/SimpleDevelopersLogPage';
import { Transportation } from './components/pages/Transportation';
import UserManagementPage from './components/pages/UserManagementPage';
import ChangeLog from './components/pages/ChangeLog';
import DeveloperPage from './components/pages/DeveloperPage';
import { PolicyTool } from './components/pages/PolicyTool';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocationProvider, useLocation as useLocationHook } from './contexts/LocationContext';
import { UserRoleProvider } from './contexts/UserRoleContext';


function PolicyToolRoute() {
  // Policy Tool is now available to all users
  return <PolicyTool />;
}

function AppContent() {
  const { selectedLocation, selectLocation } = useLocationHook();
  const { loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Allow unauthenticated access - no login requirement

  // Check if we're on the landing page (no navigation)
  const isLandingPage = location.pathname === '/';
  
  // Check if we're on the CoolClimate Calculator page
  const isCoolClimatePage = location.pathname === '/coolclimate';

  const handleLocationSelect = (selectedLocationName: string) => {
    selectLocation(selectedLocationName);
    // If on landing page (/), navigate to Overview page (/overview) to show dashboard
    if (location.pathname === '/') {
      navigate('/overview');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Otherwise, stay on current page - location change will update data
    // Do not scroll to top when changing location on other pages - maintain user's scroll position
  };

  const handleNavigateToOverview = () => {
    navigate('/');
    // Scroll to top when navigating to overview
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSectionChange = (section: string) => {
    // Check if we're on Overview page and if it's fully loaded
    const isOnOverviewPage = location.pathname === '/' || location.pathname === '/overview';
    const overviewFullyLoaded = typeof window !== 'undefined' && (window as any).overviewFullyLoaded === true;
    
    // If on Overview page and not fully loaded, block navigation to pages other than Transportation
    if (isOnOverviewPage && !overviewFullyLoaded && section !== 'transportation' && section !== 'overview') {
      alert('Please wait until the Overview page fully loads before navigating to other pages. The Transportation page is available.');
      return;
    }
    
    // Handle section change
    if (section === 'people') {
      navigate('/people');
    } else if (section === 'overview') {
      navigate('/');
    } else if (section === 'ghg') {
      navigate('/ghg');
    } else if (section === 'buildings') {
      navigate('/buildings');
    } else if (section === 'transportation') {
      navigate('/transportation');
    } else if (section === 'policy-tool') {
      navigate('/policy-tool');
    } else if (section === 'community') {
      navigate('/community');
    } else if (section === 'other') {
      navigate('/other');
    }
    // Scroll to top when navigating to a new section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="App">
      <Header 
        onNavigateToOverview={handleNavigateToOverview}
        onLocationSelect={handleLocationSelect}
        currentLocation={selectedLocation}
      />
      {/* Show appropriate navigation based on page */}
      {/* Landing page (/) has no navigation, Overview dashboard (/overview) shows navigation */}
      {!isLandingPage && !isCoolClimatePage && <Navigation onSectionChange={handleSectionChange} />}
      {isCoolClimatePage && <CoolClimateNavigation onSectionChange={handleSectionChange} />}
      
      <main className="main-content">
        <Routes>
          <Route 
            path="/" 
            element={
              <Overview onLocationSelect={handleLocationSelect} isLandingPage={true} />
            } 
          />
          <Route 
            path="/overview" 
            element={
              <Overview onLocationSelect={handleLocationSelect} isLandingPage={false} />
            } 
          />
          <Route 
            path="/people" 
            element={
              <People location={selectedLocation} />
            } 
          />
          <Route 
            path="/ghg" 
            element={
              <GHGInventory location={selectedLocation} />
            } 
          />
          <Route 
            path="/buildings" 
            element={
              <Energy location={selectedLocation} />
            } 
          />
          <Route 
            path="/transportation" 
            element={
              <Transportation location={selectedLocation} />
            } 
          />
          {/* Policy Tool route - available to all users */}
          <Route
            path="/policy-tool"
            element={<PolicyToolRoute />}
          />
          <Route
            path="/community"
            element={
              <Community />
            }
          />
          <Route
            path="/changelog"
            element={
              <ChangeLog />
            }
          />
          <Route
            path="/other"
            element={
              <Other />
            }
          />
          <Route 
            path="/coolclimate" 
            element={
              <CoolClimateCalculator />
            } 
          />
                      <Route 
                        path="/developers-log" 
                        element={
                          <SimpleDevelopersLogPage />
                        } 
                      />
          <Route 
            path="/user-management" 
            element={
              <UserManagementPage />
            } 
          />
          <Route 
            path="/developer/:developerSlug" 
            element={
              <DeveloperPage />
            } 
          />
          <Route 
            path="/auth/callback" 
            element={
              <AuthCallback />
            } 
          />
          <Route 
            path="/auth/reset-password" 
            element={
              <PasswordReset />
            } 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      
      {/* Footer - Appears on all pages */}
      <Footer />
    </div>
  );
}

function AppWithUserRole() {
  const { user } = useAuth();
  
  // Always render AppContent, with or without user
  // UserRoleProvider will handle the case where userId is null/undefined
  return (
    <UserRoleProvider userId={user?.id}>
      <AppContent />
    </UserRoleProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <LocationProvider>
        <Router>
          <AppWithUserRole />
        </Router>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;